import {IssueGroupingKey, StoppageCode, useStoppageCodeStats} from '@domain/stoppages'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {Grid} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useNavigate, useParams} from 'react-router'

import {StatusPageHeader, DowntimeFilters, StoppageStatsTable, TopIssues} from '../components'
import {
  DOWNTIME_DETAILS_FILTER_OPTIONS,
  DowntimeAnalyticsEventName,
  FEATURE_NAMESPACE
} from '../consts'
import {
  useDowntimeFiltersState,
  useEquipmentDetailsTitle,
  usePlantTimeRange,
  useSortedStoppages,
  useTrackDowntimeDashboardAnalytics
} from '../hooks'
import {useDowntimePlantId} from '../providers'

type PathParameter = {
  groupId: string
  groupType: IssueGroupingKey
}

type EquipmentStoppageDetailsProps = {
  backToStoppagesBasePath: string
  downtimeStoppageDetailsForCodeBasePath: string
  stoppageConfig?: StoppageCode[]
  onShowKilnStatsClick?: () => void
}

export const EquipmentStoppageDetails: React.FC<EquipmentStoppageDetailsProps> = ({
  stoppageConfig,
  backToStoppagesBasePath,
  downtimeStoppageDetailsForCodeBasePath,
  onShowKilnStatsClick
}) => {
  const plantId = useDowntimePlantId()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()
  const {groupType, groupId} = useParams<PathParameter>()
  if (!groupId || !groupType) throw new Error('Missing stoppage group prop')
  const timeRange = usePlantTimeRange()
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)
  const timezone = useUpmPlantTimezone(plantId)

  const {
    mainEquipments,
    setMainEquipments,
    equipmentNumbers,
    setEquipmentNumbers,
    failureStoppage,
    setFailureStoppage,
    stoppageCodes,
    setStoppageCodes
  } = useDowntimeFiltersState({groupId, groupType})

  const selectedMainEquipments = useMemo(() => mainEquipments?.map((eq) => eq.id), [mainEquipments])
  const selectedEquipments = useMemo(() => equipmentNumbers?.map((eq) => eq.id), [equipmentNumbers])
  const selectedStoppedMainEquipments = useMemo(
    () => (groupType === IssueGroupingKey.STOPPED_MAIN_EQUIPMENT ? [groupId] : undefined),
    [groupType, groupId]
  )

  const {
    topIssuesSortingKey,
    setTopIssuesSortingKey,
    topIssuesGroupingKey,
    sortedStoppages,
    aggregatedStoppagesQuery
  } = useSortedStoppages({
    plantId,
    groupBy: IssueGroupingKey.STOPPAGE_CODE,
    sortingEventName: DowntimeAnalyticsEventName.UserSet3LevelSorting,
    timeRange,
    selectedStoppedMainEquipments,
    selectedMainEquipments,
    selectedStoppages: failureStoppage,
    selectedEquipmentNumbers: selectedEquipments
  })

  const stoppageCodeStatsQuery = useStoppageCodeStats(plantId, {
    ...timeRange,
    // NOTE: for equipments/mainEquipments page, groupBy is forced to STOPPAGE_CODE
    groupBy: IssueGroupingKey.STOPPAGE_CODE,
    ...(selectedStoppedMainEquipments && {selectedStoppedMainEquipments}),
    ...(mainEquipments && {selectedMainEquipments}),
    ...(equipmentNumbers && {selectedEquipments}),
    ...(failureStoppage && {selectedStoppages: failureStoppage}),
    ...(stoppageCodes && {selectedStoppageCodes: stoppageCodes})
  })

  const title = useEquipmentDetailsTitle({
    groupType,
    groupId,
    stoppageStatsData: stoppageCodeStatsQuery.data
  })

  return (
    <>
      <StatusPageHeader
        title={title}
        backButtonProps={{
          target: generatePath(backToStoppagesBasePath, {plantId}),
          targetName: t('actions.backToStoppages')
        }}
        filterComponent={
          <DowntimeFilters
            filteringEventName={DowntimeAnalyticsEventName.UserAdjusts3LevelFilter}
            failureState={{failureStoppage, setFailureStoppage}}
            mainEquipmentState={{mainEquipments, setMainEquipments}}
            stoppageCodesState={{stoppageCodes, setStoppageCodes}}
            equipmentNumbersState={{equipmentNumbers, setEquipmentNumbers}}
            options={DOWNTIME_DETAILS_FILTER_OPTIONS[groupType]}
          />
        }
      />
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <TopIssues
            timeRange={timeRange}
            stoppageConfig={stoppageConfig}
            title={t('label.downtimes')}
            hideInfoIcon
            stoppageCodeStats={stoppageCodeStatsQuery.data || {}}
            showFramesLabelFromTimeRange
            topIssuesSortingKey={topIssuesSortingKey}
            onTopIssuesSortingKeyChange={setTopIssuesSortingKey}
            topIssuesGroupingKey={topIssuesGroupingKey}
            disableGroupingKey
            sortedStoppages={sortedStoppages}
            aggregatedStoppagesQuery={aggregatedStoppagesQuery}
            onBarClick={(stoppageCode?: string) => {
              if (!stoppageCode) return

              trackAnalyticsEvent(DowntimeAnalyticsEventName.UserClicksOn3LevelStoppagesGraphBar, {
                groupId,
                group: topIssuesGroupingKey
              })
              void navigate(
                generatePath(downtimeStoppageDetailsForCodeBasePath, {
                  plantId,
                  groupType,
                  groupId,
                  stoppageCode
                }),
                {relative: 'path'}
              )
            }}
            onShowKilnStatsClick={onShowKilnStatsClick}
          />
        </Grid>
        <Grid item md={12} xs={12}>
          <StoppageStatsTable
            stoppageCodeStatsQuery={stoppageCodeStatsQuery}
            stoppageConfig={stoppageConfig}
            topIssuesGroupingKey={topIssuesGroupingKey}
            sortedStoppages={sortedStoppages}
            timezone={timezone}
          />
        </Grid>
      </Grid>
    </>
  )
}
